'use client';

import { useEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTranslation } from '@/i18n/LanguageProvider';

/**
 * Botón de modo día / noche.
 *
 * La clase `dark` del <html> la fija ya el script anti-parpadeo de
 * layout.tsx antes del primer paint, así que los iconos sol/luna se
 * muestran con `dark:hidden` / `hidden dark:block` (sin flash). Este
 * componente solo alterna la clase y persiste la preferencia.
 */
export default function ThemeToggle({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    setIsDark(document.documentElement.classList.contains('dark'));
  }, []);

  const toggle = () => {
    const root = document.documentElement;
    const next = !root.classList.contains('dark');
    root.classList.toggle('dark', next);
    setIsDark(next);
    try {
      localStorage.setItem('theme', next ? 'dark' : 'light');
    } catch {
      /* localStorage no disponible (modo privado): solo cambio en memoria */
    }
  };

  const label = isDark ? t('nav.lightMode') : t('nav.darkMode');

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      title={label}
      className={`p-2 rounded-lg text-gray-600 hover:text-primary hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors ${className}`}
    >
      <Moon className="w-5 h-5 dark:hidden" />
      <Sun className="w-5 h-5 hidden dark:block" />
      <span className="sr-only">{label}</span>
    </button>
  );
}
